import React from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import {
  addMeasureCohortFilter,
  builderSetRollingControl,
  ICohortRow,
  IRow,
  IRowMeta,
  removeRowAction,
  RowType,
  setRowPropAction,
} from '../../../../redux/slices/oldBuilder';
import {
  measureRowsSelector,
  rowSelector,
} from '../../../../redux/selectors/builder';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  HighlightedEntity,
  useHighlightingContext,
} from '../../../HighlightingContext';
import useNamedMeasureFromSearchParams from './useNamedMeasureFromSearchParams';
import { RollingControlFunction } from '../../../../utils/apollo/graphqlSchema';
import { BuilderRowActions } from '../../BuilderActions';
import MeasureOptions from '../MeasureOptions';
import styled from 'styled-components';
import DragHandle from '../../DragHandle';
import NamedMeasureSelector from './NamedMeasureSelector';
import RollingControl from '../RollingControl';
import BuilderNamedCohort from '../../Cohorts/CohortRow/BuilderNamedCohort';
import isNil from 'lodash/isNil';
import { useActionsVisibilityContext } from '../../../ActionsVisibilityContext';
import ThinDivider from '../../../ThinDivider';
import { theme } from '../../../../styles/theme';
import BadgeRound from '../../../BadgeRound';
import { getCharacterByIncrementedIndex } from '../../../../utils/character';
import { SquareRemoveButton } from '../../../Button';

interface IProps {
  rowMeta: IRowMeta;
  cohortRowMeta: IRowMeta;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}

const Container = styled(HighlightedEntity)`
  display: flex;
  align-items: center;
  min-height: 48px;
  padding-right: 16px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background-color: ${theme.colors.tableHeader};
`;

const DragHandleContainer = styled.div`
  flex: 0 0 auto;
  width: 16px;
`;

const MeasureOptionsContent = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
  row-gap: 8px;
  flex-wrap: wrap;
`;

const MeasureLabel = styled(BadgeRound)`
  margin-right: 8px;
`;

const MeasureRowHeader = ({
  rowMeta,
  cohortRowMeta,
  dragHandleProps,
}: IProps) => {
  const dispatch = useAppDispatch();

  const { actionsVisible, hideActions } = useActionsVisibilityContext();

  const { rowType, rowIndex } = rowMeta;

  const { activateHighlighting, deactivateHighlighting } =
    useHighlightingContext();

  // check search params for named measure data and populate
  useNamedMeasureFromSearchParams();

  const measures = useAppSelector((state) =>
    measureRowsSelector(state, rowType),
  );

  const currentMeasure: IRow = useAppSelector((state) =>
    rowSelector(state, rowMeta),
  );

  const { showMeasure, measureRollingControl, showNamedCohort } =
    currentMeasure;

  const cohortRow: ICohortRow = useAppSelector((state) =>
    rowSelector(state, cohortRowMeta),
  );

  const isDataTable = rowType === RowType.dataTableMeasures;

  const isSimpleMeasure = measures.length === 1;

  const allowRollingControl = !isDataTable && isSimpleMeasure;

  const canRemoveMeasure = !isSimpleMeasure;

  const onRemoveButtonClick = () => {
    dispatch(removeRowAction(rowMeta));

    deactivateHighlighting();
    hideActions();
  };

  const handleAddRollingControl = (rollingFunction: RollingControlFunction) => {
    dispatch(
      builderSetRollingControl({
        rowMeta,
        value: {
          __typename: 'RollingControl',
          function: rollingFunction,
          window: 0,
        },
      }),
    );
  };

  const handleShowCohort = () => {
    dispatch(
      setRowPropAction({
        rowMeta,
        row: { showNamedCohort: true },
      }),
    );
  };

  const handleHideCohort = () => {
    dispatch(
      setRowPropAction({
        rowMeta,
        row: { showNamedCohort: false },
      }),
    );
  };

  const handleSetupCustomCohort = () => {
    handleHideCohort();
    dispatch(
      addMeasureCohortFilter({
        rowMeta: cohortRowMeta,
      }),
    );
  };

  return (
    <Container>
      <DragHandleContainer>
        {dragHandleProps && (
          <DragHandle
            dragHandleProps={dragHandleProps}
            visible={actionsVisible}
          />
        )}
      </DragHandleContainer>
      <MeasureLabel size="regular">
        {getCharacterByIncrementedIndex('A', rowIndex)}
      </MeasureLabel>
      <MeasureOptionsContent>
        <MeasureOptions
          rowMeta={rowMeta}
          onAddCohort={handleShowCohort}
          onAddRolling={allowRollingControl ? handleAddRollingControl : null}
          disableCohortOption={cohortRow?.isActive || showNamedCohort || false}
          disableRollingOption={!isNil(measureRollingControl)}
        />
        {showMeasure && (
          <>
            <ThinDivider layout="vertical" size="short" />
            <NamedMeasureSelector
              rowMeta={rowMeta}
              cohortRow={cohortRow}
              isCohort={false}
              allowRollingControl={allowRollingControl}
              allowCompoundMeasure={isSimpleMeasure || isDataTable}
            />
          </>
        )}
        {showNamedCohort && (
          <>
            <ThinDivider layout="vertical" size="short" />
            <BuilderNamedCohort
              rowMeta={cohortRowMeta}
              onCreateCustom={handleSetupCustomCohort}
              onClose={handleHideCohort}
              onRemove={handleHideCohort}
              showMenuByDefault
            />
          </>
        )}
        {measureRollingControl && (
          <>
            <ThinDivider layout="vertical" size="short" />
            <RollingControl
              rowMeta={rowMeta}
              measureRollingControl={measureRollingControl}
            />
          </>
        )}
      </MeasureOptionsContent>

      {actionsVisible && canRemoveMeasure && (
        <BuilderRowActions>
          <SquareRemoveButton
            onClick={onRemoveButtonClick}
            onMouseEnter={activateHighlighting}
            onMouseLeave={deactivateHighlighting}
            content={'Remove Measure'}
          />
        </BuilderRowActions>
      )}
    </Container>
  );
};

export default MeasureRowHeader;
